import React from 'react';

/**
 * A small Markdown preview for the email editor: headings, bullet and numbered lists, bold,
 * italic and links. It builds React elements (never HTML strings), so nothing a template says
 * can run. The real email is made by `send-emails`; this only shows the shape of the text.
 */

/** Replaces every {{variable}} with its example value (unknown ones stay as they are). */
export function fillVariables(text: string, vars: Record<string, string>): string {
  return text.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (whole, name: string) => vars[name] ?? whole);
}

const SAFE_LINK = /^(https?:\/\/|mailto:)/i;
const INLINE = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|\[[^\]]+\]\([^)\s]+\))/;

function inline(text: string, key: string): React.ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    const k = `${key}-${i}`;
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={k}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={k}>{part.slice(1, -1)}</em>;
    }
    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
    if (link) {
      return SAFE_LINK.test(link[2]) ? (
        <a key={k} href={link[2]} target="_blank" rel="noreferrer">
          {link[1]}
        </a>
      ) : (
        link[1]
      );
    }
    return part;
  });
}

export function renderMarkdown(source: string, vars: Record<string, string> = {}): React.ReactNode {
  const text = fillVariables(source.replace(/\r\n/g, '\n'), vars).trim();
  if (!text) return null;
  return text.split(/\n{2,}/).map((block, i) => {
    const key = `b${i}`;
    const lines = block.split('\n');
    const heading = /^(#{1,3})\s+(.*)$/.exec(lines[0]);
    if (heading && lines.length === 1) {
      const size = [20, 17, 15][heading[1].length - 1];
      return (
        <div key={key} style={{ fontSize: size, fontWeight: 700, margin: '0 0 8px' }}>
          {inline(heading[2], key)}
        </div>
      );
    }
    if (lines.every((l) => /^[-*]\s+/.test(l))) {
      return (
        <ul key={key} style={{ margin: '0 0 12px', paddingLeft: 22 }}>
          {lines.map((l, j) => (
            <li key={j}>{inline(l.replace(/^[-*]\s+/, ''), `${key}-${j}`)}</li>
          ))}
        </ul>
      );
    }
    if (lines.every((l) => /^\d+\.\s+/.test(l))) {
      return (
        <ol key={key} style={{ margin: '0 0 12px', paddingLeft: 22 }}>
          {lines.map((l, j) => (
            <li key={j}>{inline(l.replace(/^\d+\.\s+/, ''), `${key}-${j}`)}</li>
          ))}
        </ol>
      );
    }
    return (
      <p key={key} style={{ margin: '0 0 12px' }}>
        {lines.map((l, j) => (
          <React.Fragment key={j}>
            {j > 0 && <br />}
            {inline(l, `${key}-${j}`)}
          </React.Fragment>
        ))}
      </p>
    );
  });
}
